import { createSelector } from '@reduxjs/toolkit';
import { maxBy, minBy } from 'lodash';
import { DefaultRootState } from 'react-redux';
import {
  allowedTagsSelector,
  showShrSelector,
} from '../../auth/selectors/auth.selectors';
import { leadNamesSelector } from '../../ecg/selectors/signal.selectors';
import { getLayoutValueSelector } from '../../layout/selectors/layout.selectors';
import { BPM, MS } from '../../layout/types/layout-state.type';
import { Metric } from '../../metric/types/metric.type';
import { LaddergramLink } from '../../plot/types/laddergram.type';
import { isLinkable } from '../../plot/utils/laddergram';
import { MINUTE } from '../../utils/time';
import { selectAll, selectEntities } from '../reducers/wave.reducer';
import { BeatInterval, isValidShrInterval } from '../types/interval.type';
import { Wave, WaveId, WaveTypeMapping } from '../types/wave.type';
import {
  getAllowedTagContainers,
  getAssociativeTagContainers,
  getTagContainers,
} from '../utils/tags.utils';
import {
  createShrInterval,
  DEFAULT_WAVE_LENGTH,
  getUpdatedWave,
  getWaveDuration,
  getWaveLeads,
  invalidTypes,
  isWaveOfType,
  sortWavesByLayers,
} from '../utils/wave.util';
import { updateSelector } from './update.selectors';
import { exactDurationSelector } from '../../ecg/selectors/ecg.selectors';

export const waveStateSelector = ({ wave }: DefaultRootState) => wave;

export const waveListSelector = createSelector(waveStateSelector, selectAll);

export const wavesEntitiesSelector = createSelector(
  waveStateSelector,
  selectEntities,
);

export const selectedIdsSelector = createSelector(
  waveStateSelector,
  ({ selectedIds }) => selectedIds,
);

export const savedWavesSelector = createSelector(
  waveStateSelector,
  ({ savedWaves }) => savedWaves,
);

export const selectedWavesSelector = createSelector(
  selectedIdsSelector,
  wavesEntitiesSelector,
  (selectedIds, entities) => {
    const waves: Wave[] = [];
    for (const id of selectedIds) {
      const wave = entities[id];
      if (wave == null) {
        continue;
      }
      waves.push(wave);
    }
    return waves;
  },
);

export const medianDurationsSelector = (time: number) =>
  createSelector(beatWaveListSelector, (waves) => {
    // we group the wave durations by type
    const waveDurationsByType: WaveTypeMapping<number[]> = {
      P: [],
      QRS: [],
      T: [],
      calibration: [],
      artifact: [],
      noise: [],
      no_signal: [],
      p_level_noise: [],
      qrs_level_noise: [],
      pt_level_noise: [],
      uncertain: [],
      microvolted: [],
    };

    const filteredWaves = waves.filter(({ offset }) => offset < time);
    for (const wave of filteredWaves) {
      waveDurationsByType[wave.type].push(getWaveDuration(wave));
    }

    // we pre-fill the result with the default value in case we can't find a median
    const mediansByType: WaveTypeMapping<number> = {
      P: DEFAULT_WAVE_LENGTH.P,
      QRS: DEFAULT_WAVE_LENGTH.QRS,
      T: DEFAULT_WAVE_LENGTH.T,
      calibration: DEFAULT_WAVE_LENGTH.calibration,
      artifact: DEFAULT_WAVE_LENGTH.artifact,
      noise: DEFAULT_WAVE_LENGTH.noise,
      no_signal: DEFAULT_WAVE_LENGTH.no_signal,
      p_level_noise: DEFAULT_WAVE_LENGTH.p_level_noise,
      qrs_level_noise: DEFAULT_WAVE_LENGTH.qrs_level_noise,
      pt_level_noise: DEFAULT_WAVE_LENGTH.pt_level_noise,
      uncertain: DEFAULT_WAVE_LENGTH.uncertain,
      microvolted: DEFAULT_WAVE_LENGTH.microvolted,
    };

    // for each type, we look for the median duration
    for (const type of Object.keys(
      waveDurationsByType,
    ) as (keyof typeof waveDurationsByType)[]) {
      const waves = waveDurationsByType[type].sort((a, b) => a - b);
      if (waves.length > 0) {
        const medianIndex = Math.floor((waves.length - 1) / 2);
        mediansByType[type] = waves[medianIndex];
      }
    }

    return mediansByType;
  });

export const waveForIdSelector = (waveId?: WaveId) =>
  createSelector(wavesEntitiesSelector, (waveState) =>
    waveId != null ? waveState[waveId] : undefined,
  );

export const selectedSelector = (waveId: WaveId) =>
  createSelector(selectedIdsSelector, (selectedIds) =>
    selectedIds.includes(waveId),
  );

export const waveForIdsSelector = (waveIds: WaveId[]) =>
  createSelector(wavesEntitiesSelector, (waveState) =>
    waveIds
      .map((waveId) => waveState[waveId])
      .filter((wave): wave is Wave => wave != null),
  );

export const updatedWaveListSelector = createSelector(
  waveStateSelector,
  updateSelector,
  exactDurationSelector,
  ({ entities, ids }, update, ecgDuration) =>
    ids
      .map((id) => entities[id])
      .filter((wave): wave is Wave => wave != null)
      .map((wave) => getUpdatedWave(wave, update, ecgDuration)),
);

export const beatWaveListSelector = createSelector(waveListSelector, (waves) =>
  waves.filter((wave) => isWaveOfType(wave, 'P', 'QRS', 'T')),
);

export const qrsWaveListSelector = createSelector(waveListSelector, (waves) =>
  waves.filter((wave) => isWaveOfType(wave, 'QRS')),
);

export const pWaveListSelector = createSelector(waveListSelector, (waves) =>
  waves.filter((wave) => isWaveOfType(wave, 'P')),
);

export const qrsTWaveListSelector = createSelector(waveListSelector, (waves) =>
  waves.filter((wave) => isWaveOfType(wave, 'QRS', 'T')),
);

export const pQrsWaveListSelector = createSelector(waveListSelector, (waves) =>
  waves.filter((wave) => isWaveOfType(wave, 'P', 'QRS')),
);

export const noiseWaveListSelector = createSelector(waveListSelector, (waves) =>
  waves.filter((wave) => isWaveOfType(wave, ...invalidTypes)),
);

export const noiseWaveListForTagSelector = (tagFilter?: string) =>
  createSelector(waveListSelector, (waves) => {
    const noiseWaves = waves.filter((wave) =>
      isWaveOfType(wave, ...invalidTypes),
    );
    return tagFilter == null
      ? noiseWaves
      : noiseWaves.filter((wave) => wave.tags.includes(tagFilter));
  });

export const waveLinksSelector = createSelector(
  beatWaveListSelector,
  (waves) => {
    return waves
      .filter((wave) => isLinkable(wave))
      .reduce((waveLinks: LaddergramLink[], currentWave, index, list) => {
        const nextWave = list[index + 1];
        if (
          nextWave != null &&
          isWaveOfType(currentWave, 'P') &&
          isWaveOfType(nextWave, 'QRS') &&
          nextWave.onset - currentWave.offset < 2000
        ) {
          waveLinks.push({
            pWave: currentWave,
            qrsWave: nextWave,
          });
        }
        return waveLinks;
      }, []);
  },
);

export const layeredQrsWavesSelector = createSelector(
  beatWaveListSelector,
  (waves) => sortWavesByLayers(waves),
);

export const noiseWavesByLeadSelector = createSelector(
  noiseWaveListSelector,
  leadNamesSelector,
  (waves, leadNames) => {
    const wavesByLead: { [lead: string]: Wave[] } = {};

    leadNames.forEach((lead) => (wavesByLead[lead] = []));

    waves.forEach((wave) => {
      const leads = getWaveLeads(wave, leadNames);
      leads.forEach((lead) => wavesByLead[lead].push(wave));
    });

    return wavesByLead;
  },
);

export const noiseWaveForLeadSelector = (leads: string[], tagFilter?: string) =>
  createSelector(noiseWavesByLeadSelector, (wavesByLead) => {
    const noiseWaves = leads.flatMap((lead) => wavesByLead[lead]);

    return tagFilter == null
      ? noiseWaves
      : noiseWaves.filter((wave) => wave.tags.includes(tagFilter));
  });

export const layeredNoiseWaveForLeadSelector = (
  leads: string[],
  tagFilter?: string,
) =>
  createSelector(noiseWaveForLeadSelector(leads, tagFilter), (waves) =>
    sortWavesByLayers(waves),
  );

export const isAllLeadsNoiseAnnotatedSelector = createSelector(
  noiseWavesByLeadSelector,
  (wavesByLead) =>
    Object.values(wavesByLead).every(
      (invWaves) => Array.isArray(invWaves) && invWaves.length > 0,
    ),
);

export const leadsWithoutNoiseAnnotationSelector = createSelector(
  noiseWavesByLeadSelector,
  (wavesByLead) =>
    Object.entries(wavesByLead)
      .filter(
        ([_, invWaves]) => !Array.isArray(invWaves) || invWaves.length === 0,
      )
      .map(([leadName]) => leadName),
);

export const intervalFormatterSelectorFunction = createSelector(
  getLayoutValueSelector('showHrInBpm'),
  (showHrInBpm) => (duration: number) =>
    showHrInBpm ? MINUTE / duration : duration,
);

export const intervalSelector = createSelector(
  qrsWaveListSelector,
  intervalFormatterSelectorFunction,
  (waves, intervalFormatter) => {
    return waves
      .map((wave, index, qrsWaves): BeatInterval | null => {
        const prevWave = qrsWaves[index - 1];
        if (prevWave == null) {
          return null;
        }
        return {
          timestamp: (wave.onset + prevWave.onset) / 2,
          duration: intervalFormatter(wave.onset - prevWave.onset),
        };
      })
      .filter((interval): interval is BeatInterval => interval != null);
  },
);

export const millisecondsIntervalSelector = createSelector(
  qrsWaveListSelector,
  (waves) =>
    waves
      .map((wave, index, qrsWaves): BeatInterval | null => {
        const prevWave = qrsWaves[index - 1];
        if (prevWave == null || wave.tags.includes('noisy_rr')) {
          return null;
        }
        return {
          timestamp: (wave.onset + prevWave.onset) / 2,
          duration: wave.onset - prevWave.onset,
        };
      })
      .filter((duration): duration is BeatInterval => duration != null),
);

export const getLongestRRSelector = createSelector(
  millisecondsIntervalSelector,
  (RRs) => {
    const longestRR = maxBy(RRs, (RR) => RR.duration);
    if (longestRR == null || longestRR.duration == null) {
      return;
    }
    return Math.round(longestRR.duration);
  },
);

// the interval selector has the 0 values removed for the shr axis and the min and max metric
export const shrIntervalSelector = createSelector(
  updatedWaveListSelector,
  (waves) => createShrInterval(waves).filter(isValidShrInterval),
);

export const intervalMinMaxShrSelector = createSelector(
  shrIntervalSelector,
  (shrIntervals) => [
    minBy(shrIntervals, (shrInterval) => shrInterval.shr),
    maxBy(shrIntervals, (shrInterval) => shrInterval.shr),
  ],
);

// the array selector has all the values including 0 for the calculation of VR, VT
// waves with invalid Shrs should not be ignored in this case because you might have an edge case
// of a beat being ignored and the rest aggregate 4 consecutive beats with a pvc then
// it will produce a VR or a VT where in fact it is not because there is a beat somewhere in the middle
// that has been ignored
export const shrArraySelector = createSelector(
  updatedWaveListSelector,
  (waves) => createShrInterval(waves),
);

export const metricsSelector = createSelector(
  intervalMinMaxShrSelector,
  getLongestRRSelector,
  showShrSelector,
  ([minShr, maxShr], longestRR, showShr) => {
    const metrics: Metric[] = [];

    if (showShr) {
      if (minShr != null) {
        metrics.push({
          description: 'Min SHR',
          unit: BPM,
          value: minShr.shr,
          color: 'purple.500',
        });
      }
      if (maxShr != null) {
        metrics.push({
          description: 'Max SHR',
          unit: BPM,
          value: maxShr.shr,
          color: 'purple.500',
        });
      }
    }

    if (longestRR != null) {
      metrics.push({
        description: 'Longest RR',
        unit: MS,
        value: longestRR,
        color: 'neutral.900',
      });
    }
    return metrics;
  },
);

export const tagsForWaveSelector = (waveId: WaveId) =>
  createSelector(
    waveForIdSelector(waveId),
    allowedTagsSelector('tagsToAnnotate'),
    (wave, tags) => {
      if (wave == null) {
        return [];
      }

      if (isWaveOfType(wave, 'QRS', 'P')) {
        return getAllowedTagContainers(tags, wave.type);
      } else {
        return getTagContainers(wave.type);
      }
    },
  );

export const associativeTagsForWaveSelector = (waveId: WaveId) =>
  createSelector(
    waveForIdSelector(waveId),
    allowedTagsSelector('tagsToAnnotate'),
    (wave, tags) => {
      if (wave == null) {
        return [];
      }

      return getAssociativeTagContainers(tags, wave.type);
    },
  );
